// VRG-679 — the Outreach page's dialogs: create/edit a campaign, add/edit a target brand, link a
// demo. House components only (§28/§31): `@repo/ui` Modal / Button, the platform `Field`,
// `TextAreaField`, `SelectField` and `DatePickerField` (react-hook-form), `FormSubmitButton`
// for the Save-until-dirty rule (§54).
import React, { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Stack, Typography } from '@mui/material'
import {
  OUTREACH_CAMPAIGN_TYPES,
  OUTREACH_CAMPAIGN_TYPE_LABELS,
  OUTREACH_TARGET_STATUSES,
  OUTREACH_TARGET_STATUS_LABELS,
  DEFAULT_REPORTING_CURRENCY,
  REPORTING_CURRENCIES,
  type OutreachTargetStatus,
} from '@repo/types'
import { Button, Modal } from '@repo/ui'

import { DatePickerField, Field, FormSubmitButton, SelectField, TextAreaField } from 'components'
import { useDemos } from 'hooks/api/useDemos'
import type { OutreachCampaign, OutreachCampaignInput, OutreachTarget, OutreachTargetInput } from 'services'

import { OUTREACH_PLAYBOOK } from './outreachPlaybook.generated'

const TYPE_OPTIONS = OUTREACH_CAMPAIGN_TYPES.map((t) => ({ value: t, label: OUTREACH_CAMPAIGN_TYPE_LABELS[t] }))
const STATUS_OPTIONS = OUTREACH_TARGET_STATUSES.map((s) => ({ value: s, label: OUTREACH_TARGET_STATUS_LABELS[s] }))
/** The industries the snapshotted templates have an opening for, so a target maps to one. */
export const INDUSTRY_OPTIONS = [
  { value: '', label: 'Not set' },
  ...OUTREACH_PLAYBOOK.templates.openings.map((o) => ({ value: o.industry, label: o.industry })),
]

const DAY = 'YYYY-MM-DD'
const blank = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : null)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Untyped = any

type CampaignForm = {
  name: string
  type: string
  city: string
  starts_on: string
  ends_on: string
  notes: string
  trip_cost_amount: string
  trip_cost_currency: string
}

const CURRENCY_OPTIONS = REPORTING_CURRENCIES.map((c) => ({ value: c.code, label: `${c.code} · ${c.label}` }))
const MONEY = /^\d{1,10}(\.\d{1,2})?$/

/**
 * VRG-684 — the target statuses after a reply, where a meeting date and notes mean something.
 * `declined` is left out: the fields stay on the record, but a "no" does not invite new notes.
 */
export const MEETING_STATUSES: ReadonlySet<OutreachTargetStatus> = new Set([
  'replied',
  'meeting_booked',
  'meeting_held',
  'pilot',
  'loi_signed',
])

export const CampaignModal = ({
  open,
  campaign,
  loading,
  onClose,
  onSave,
}: {
  open: boolean
  /** Absent = create. */
  campaign?: OutreachCampaign | null
  loading: boolean
  onClose: () => void
  onSave: (input: OutreachCampaignInput) => void
}) => {
  const methods = useForm<CampaignForm>()
  const { control, reset, handleSubmit, watch } = methods
  useEffect(() => {
    if (!open) return
    reset({
      name: campaign?.name ?? '',
      type: campaign?.type ?? 'in_person',
      city: campaign?.city ?? '',
      starts_on: campaign?.starts_on ?? '',
      ends_on: campaign?.ends_on ?? '',
      notes: campaign?.notes ?? '',
      trip_cost_amount: campaign?.trip_cost ? String(campaign.trip_cost.amount) : '',
      trip_cost_currency: campaign?.trip_cost?.currency ?? DEFAULT_REPORTING_CURRENCY,
    })
  }, [open, campaign, reset])
  const type = watch('type')
  const submit = handleSubmit((v) =>
    onSave({
      name: v.name.trim(),
      type: v.type as OutreachCampaignInput['type'],
      city: blank(v.city),
      starts_on: blank(v.starts_on),
      ends_on: blank(v.ends_on),
      notes: blank(v.notes),
      // Amount and currency travel together; an empty amount clears the cost.
      trip_cost: blank(v.trip_cost_amount)
        ? { amount: Number(v.trip_cost_amount.trim()), currency: v.trip_cost_currency || DEFAULT_REPORTING_CURRENCY }
        : null,
    }),
  )
  const m = methods as Untyped
  return (
    <Modal
      title={campaign ? 'Edit campaign' : 'New campaign'}
      open={open}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <FormSubmitButton control={control} requireDirty={!!campaign} onClick={() => submit()} loading={loading}>
            {campaign ? 'Save' : 'Create'}
          </FormSubmitButton>
        </>
      }
    >
      <Stack spacing={4} mt={2}>
        <Field {...m} name="name" label="Name" placeholder="Chicago, October" rules={{ required: 'Give the campaign a name' }} />
        <Controller
          control={control}
          name="type"
          render={({ field }) => (
            <SelectField label="Type" options={TYPE_OPTIONS} value={field.value} onChange={(v) => field.onChange(String(v))} />
          )}
        />
        <Field {...m} name="city" label={type === 'in_person' ? 'City' : 'City (optional)'} placeholder="Chicago" />
        <Stack direction="row" spacing={3}>
          <DatePickerField {...m} name="starts_on" label="From" format={DAY} />
          <DatePickerField {...m} name="ends_on" label="To" format={DAY} />
        </Stack>
        {/* VRG-684 — optional; what the trip cost, in the currency it was paid in. No conversion. */}
        <Stack direction="row" spacing={3}>
          <Field
            {...m}
            name="trip_cost_amount"
            label="Trip cost (optional)"
            placeholder="2400"
            type="text"
            rules={{ validate: (v: string) => !v?.trim() || MONEY.test(v.trim()) || 'A number, up to two decimals' }}
          />
          <Controller
            control={control}
            name="trip_cost_currency"
            render={({ field }) => (
              <SelectField label="Currency" options={CURRENCY_OPTIONS} value={field.value} onChange={(v) => field.onChange(String(v))} />
            )}
          />
        </Stack>
        <TextAreaField {...m} name="notes" label="Notes" placeholder="Hotel, who is going" />
      </Stack>
    </Modal>
  )
}

type TargetForm = {
  brand_name: string
  contact_name: string
  contact_email: string
  industry: string
  website: string
  status: string
  meeting_at: string
  meeting_notes: string
  notes: string
}

export const TargetModal = ({
  open,
  target,
  loading,
  onClose,
  onSave,
}: {
  open: boolean
  target?: OutreachTarget | null
  loading: boolean
  onClose: () => void
  onSave: (input: OutreachTargetInput) => void
}) => {
  const methods = useForm<TargetForm>()
  const { control, reset, handleSubmit, watch } = methods
  useEffect(() => {
    if (!open) return
    reset({
      brand_name: target?.brand_name ?? '',
      contact_name: target?.contact_name ?? '',
      contact_email: target?.contact_email ?? '',
      industry: target?.industry ?? '',
      website: target?.website ?? '',
      status: target?.status ?? 'to_contact',
      meeting_at: target?.meeting_at ? target.meeting_at.slice(0, 10) : '',
      meeting_notes: target?.meeting_notes ?? '',
      notes: target?.notes ?? '',
    })
  }, [open, target, reset])
  const status = watch('status')
  const industryOptions = useMemo(() => {
    const current = target?.industry
    return current && !INDUSTRY_OPTIONS.some((o) => o.value === current)
      ? [...INDUSTRY_OPTIONS, { value: current, label: current }]
      : INDUSTRY_OPTIONS
  }, [target])
  const submit = handleSubmit((v) =>
    onSave({
      brand_name: v.brand_name.trim(),
      contact_name: blank(v.contact_name),
      contact_email: blank(v.contact_email),
      industry: blank(v.industry),
      website: blank(v.website),
      status: v.status as OutreachTargetInput['status'],
      // A meeting is a DAY here; noon UTC so it reads as the same date in every US timezone.
      meeting_at: blank(v.meeting_at) ? `${v.meeting_at}T12:00:00.000Z` : null,
      meeting_notes: blank(v.meeting_notes),
      notes: blank(v.notes),
    }),
  )
  const m = methods as Untyped
  return (
    <Modal
      title={target ? 'Edit brand' : 'Add brand'}
      open={open}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <FormSubmitButton control={control} requireDirty={!!target} onClick={() => submit()} loading={loading}>
            {target ? 'Save' : 'Add'}
          </FormSubmitButton>
        </>
      }
    >
      <Stack spacing={4} mt={2}>
        <Field {...m} name="brand_name" label="Brand" rules={{ required: 'Name the brand' }} />
        <Stack direction="row" spacing={3}>
          <Field {...m} name="contact_name" label="Contact" placeholder="First and last name" />
          <Field
            {...m}
            name="contact_email"
            label="Contact email"
            rules={{ pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Not an email address' } }}
          />
        </Stack>
        <Stack direction="row" spacing={3}>
          <Controller
            control={control}
            name="industry"
            render={({ field }) => (
              <SelectField label="Industry" options={industryOptions} value={field.value} placeholder="Not set" onChange={(v) => field.onChange(String(v))} />
            )}
          />
          <Field {...m} name="website" label="Website" placeholder="brand.com" />
        </Stack>
        <Controller
          control={control}
          name="status"
          render={({ field }) => (
            <SelectField label="Status" options={STATUS_OPTIONS} value={field.value} onChange={(v) => field.onChange(String(v))} />
          )}
        />
        {MEETING_STATUSES.has(status as OutreachTargetStatus) && (
          <>
            <DatePickerField {...m} name="meeting_at" label="Meeting date" format={DAY} />
            <TextAreaField {...m} name="meeting_notes" label="Meeting notes" placeholder="Who attended, what they asked, next step and date" />
          </>
        )}
        <TextAreaField {...m} name="notes" label="Notes" placeholder="Why they qualify: core item price, size, presence" />
      </Stack>
    </Modal>
  )
}

/** Pick one of the acquisition demos (Demos page) to link to a target. */
export const LinkDemoModal = ({
  open,
  brand,
  loading,
  onClose,
  onLink,
}: {
  open: boolean
  brand: string
  loading: boolean
  onClose: () => void
  onLink: (companyId: string) => void
}) => {
  const { data: demos = [], isLoading } = useDemos(false)
  const [value, setValue] = useState('')
  useEffect(() => {
    if (open) setValue('')
  }, [open])
  const options = demos.map((d) => ({ value: d.companyId, label: d.companyName, rightLabel: d.prospectDomain ?? undefined }))
  return (
    <Modal
      title="Link a demo"
      open={open}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button loading={loading} disabled={!value} onClick={() => onLink(value)}>
            Link
          </Button>
        </>
      }
    >
      <Stack spacing={4} mt={2}>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          Link the demo made for <strong>{brand}</strong>. Its company is recorded as coming from Outreach, so it counts
          under Outreach on GTM → Sales. To make a new demo, use the Demos page first.
        </Typography>
        <SelectField
          label="Demo"
          options={options}
          value={value}
          search
          placeholder={isLoading ? 'Loading demos' : options.length ? 'Choose a demo' : 'No demos yet'}
          disabled={isLoading || !options.length}
          onChange={(v) => setValue(String(v))}
        />
      </Stack>
    </Modal>
  )
}
